import { useCallback, useEffect, useState } from "react";
import placeholders from "../assets/data/imagePlaceholders.json";
import { buildPublicAssetPath } from "./publicAssetPath";

// Must match VARIANT_WIDTHS in scripts/generate-image-variants.mjs
const VARIANT_WIDTHS = [480, 960];
const ORIGINAL_WIDTH = 1920;

// `sizes` values for each layout. On phones the slot is declared smaller than
// it really is, which caps the effective pixel density at ~2x: a 3x screen
// would otherwise pull the 1920px originals for a 400px card.
export const IMAGE_SIZES = {
  card: "(max-width: 767px) 67vw, (max-width: 991px) 50vw, 420px",
  detail: "(max-width: 767px) 67vw, 800px",
  fullscreen: "100vw",
};

const variantPath = (imagePath, width) =>
  imagePath
    .replace("/assets/images/casas/", "/assets/images/casas-resized/")
    .replace(/\.\w+$/, `-${width}.webp`);

export const buildSrcSet = (imagePath) =>
  [
    ...VARIANT_WIDTHS.map(
      (width) => `${buildPublicAssetPath(variantPath(imagePath, width))} ${width}w`,
    ),
    `${buildPublicAssetPath(imagePath)} ${ORIGINAL_WIDTH}w`,
  ].join(", ");

export const getPlaceholderStyle = (imagePath) =>
  placeholders[imagePath]
    ? { backgroundImage: `url(${placeholders[imagePath]})` }
    : undefined;

// Warms the browser cache so the image is already there when it is shown.
const preloadedImages = new Set();

export const preloadImage = (imagePath, sizes) => {
  const key = `${imagePath}|${sizes}`;
  if (!imagePath || preloadedImages.has(key)) {
    return;
  }
  preloadedImages.add(key);
  const image = new Image();
  image.decoding = "async";
  image.sizes = sizes;
  image.srcset = buildSrcSet(imagePath);
  image.src = buildPublicAssetPath(imagePath);
};

// Keeps a carousel light: only the visible slide is mounted at first, and once
// it has loaded the neighbouring slides are mounted too so they are already
// decoded when the carousel moves. Slides are never unmounted again.
export const useCarouselImages = (count, { preloadPrevious = true } = {}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [mounted, setMounted] = useState(() => new Set());

  const safeActiveIndex = activeIndex < count ? activeIndex : 0;

  useEffect(() => {
    const wanted = [safeActiveIndex];
    if (isReady && count > 1) {
      wanted.push((safeActiveIndex + 1) % count);
      if (preloadPrevious) {
        wanted.push((safeActiveIndex - 1 + count) % count);
      }
    }
    setMounted((previous) =>
      wanted.every((index) => previous.has(index))
        ? previous
        : new Set([...previous, ...wanted]),
    );
  }, [safeActiveIndex, isReady, count, preloadPrevious]);

  const markReady = useCallback(() => setIsReady(true), []);

  return {
    activeIndex: safeActiveIndex,
    onSelect: setActiveIndex,
    shouldRender: (index) => mounted.has(index) || index === safeActiveIndex,
    markReady,
  };
};

// Starts downloading the property page's main photo as soon as the user shows
// intent (hover / touch) so it is usually ready when the page opens.
export const preloadPropertyCover = (property) =>
  preloadImage(property.images[0], IMAGE_SIZES.detail);

// Hero background, resized by scripts/generate-image-variants.mjs; the CSS
// picks the small one on phones (see .hero-background in index.css).
export const heroBackgroundStyle = {
  "--hero-background-small": `url(${buildPublicAssetPath("/assets/images/fundo-960.webp")})`,
  "--hero-background-large": `url(${buildPublicAssetPath("/assets/images/fundo-1600.webp")})`,
};
